import * as React from 'react';
import cx from 'classnames';
import { IGlyph, Icon, Spinner, ThemeStyle } from '@treelab/panda';

/**
 * The Button component.
 */
export interface IButtonProps {
  /** Passed down classname. */
  className?: string;
  /** Glyph for svg */
  icon?: IGlyph;
  /** Custom size if needed */
  iconSize?: { height?: number; width?: number };
  /** Optional id */
  id?: string;
  /** Space avoiding below */
  includeSpaceBelow?: boolean;
  /** Make it full width */
  isFullWidth?: boolean;
  /** Button is outline themed */
  isOutline?: boolean;
  /** Make the Button small. */
  isSmall?: boolean;
  /** Make the Button rounded. Default is true */
  isRounded?: boolean;
  /** Button has a white outline and text, the background color is dependant on parent */
  isReverse?: boolean;
  /** Text and icon are aligned left */
  isTextAlignedLeft?: boolean;
  /** Make the button disabled */
  isDisabled?: boolean;
  /** Loading state */
  isLoading?: boolean;
  /** On click action for the button. */
  onClick: () => void;
  /** Optional passed styles */
  passedStyles?: any;
  /** Content specified as children. */
  children?: React.ReactNode;
  /** Id for testing */
  testId?: string;
  /** Theme style for button colors */
  theme?: ThemeStyle;
  /** Glyph for svg */
  trailingIcon?: IGlyph;
  /** Custom size if needed */
  trailingIconSize?: { height?: number; width?: number };
  /** Type */
  type?: 'button' | 'submit' | 'reset' | undefined;
}

const OUTLINE_THEME = {
  [ThemeStyle.black]: '',
  [ThemeStyle.blue]: '',
  [ThemeStyle.gray]: '',
  [ThemeStyle.white]: 'panda-btn-outline--theme-white',
  [ThemeStyle.red]: '',
};

const FILLED_THEME = {
  [ThemeStyle.black]: '',
  [ThemeStyle.gray]: '',
  [ThemeStyle.blue]: 'panda-btn--filled--theme-blue',
  [ThemeStyle.white]: '',
  [ThemeStyle.red]: '',
};

export const Button: React.FC<IButtonProps> = ({
  className,
  children,
  includeSpaceBelow = false,
  isDisabled = false,
  isSmall = false,
  isRounded = true,
  isOutline = false,
  isReverse = false,
  icon,
  iconSize,
  id,
  isFullWidth = false,
  isLoading = false,
  isTextAlignedLeft = false,
  passedStyles,
  testId,
  trailingIcon,
  trailingIconSize,
  type = 'button',
  theme,
  onClick,
}) => {
  const combinedClass = cx({
    'panda-btn': true,
    'panda-btn--disabled': isDisabled,
    'panda-btn--rounded': isRounded,
    'panda-btn--not-rounded': !isRounded,
    'panda-btn--small': isSmall,
    'panda-btn--full-width': isFullWidth,
    'panda-btn--space-below-small': includeSpaceBelow && isSmall,
    'panda-btn--space-below-large': includeSpaceBelow && !isSmall,
    'hover-cursor': !isDisabled,
    // Normal Button styles
    [FILLED_THEME[theme || ThemeStyle.blue]]: !isOutline && !isReverse,
    // Outline styles
    'panda-btn-outline': isOutline,
    [OUTLINE_THEME[theme || ThemeStyle.white]]: isOutline,
    // Reverse style
    'panda-btn-reverse': isReverse,
    // Custom classname
    [className || '']: true,
  });
  const combinedTextClass = cx({
    'panda-typography--p1': !isSmall,
    'panda-typography--span2': isSmall,
    'panda-btn--text-left': isTextAlignedLeft,
    'space-left-medium': !!icon,
    'space-right-medium': !!trailingIcon,
  });

  return (
    <button
      disabled={isDisabled}
      data-test={testId}
      className={combinedClass}
      id={id}
      onClick={onClick}
      type={type}
      style={passedStyles && { ...passedStyles }}
    >
      {isLoading ? (
        <Spinner />
      ) : (
        <>
          {icon && <Icon icon={icon} size={iconSize || { height: isSmall ? 12 : 15 }} />}
          <span className={combinedTextClass}>{children}</span>
          {trailingIcon && (
            <Icon icon={trailingIcon} size={trailingIconSize || { height: isSmall ? 12 : 15 }} />
          )}
        </>
      )}
    </button>
  );
};
